import { useState } from "react";
import { Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TablePagination, Checkbox, IconButton, Tooltip, alpha } from "@mui/material";
import { IPedido } from "../../../../interface/IPedido";
import { IPagamentoPedido } from "../../../../interface/IPagamentoPedido";
import { LoadingContainer, NenhumPedidoContainer } from "./styles";
import { useDataHora } from "../../../../../core/hooks/useDataHora";
import { IMarketplace } from "../../../../../marketplace/interfaces/IMarketplace";
import { TipoMarketplace, TipoMarketplaceCor } from "../../../../../marketplace/enum/TipoMarketplace";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { useLetras } from "../../../../../core/hooks/useLetras";
import { StatusPedidoPilula } from "../../../../components/StatusPedidoPilula";
import { StatusPedido } from "../../../../enum/StatusPedido";
import { Edit, ReceiptLong } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { TipoPedido } from "../../../../enum/TipoPedido";
import moment from "moment";
import { AvaliacaoPedidoResumo } from "../../../../components/AvaliacaoPedido";
import { ISessaoMesa } from "../../../../interface/ISessaoMesa";

const getStatusPagamento = (pagamentos?: IPagamentoPedido[]) => {
    if (!pagamentos?.length) return { label: 'Não gerenciado', color: 'default' as const };
    const algumPago = pagamentos.some(p => p.pago === true);
    if (!algumPago) return { label: 'Não gerenciado', color: 'default' as const };
    const todosPagos = pagamentos.every(p => p.pago === true);
    if (todosPagos) return { label: 'Pago', color: 'success' as const };
    return { label: 'Pendente', color: 'warning' as const };
};

const formatarAgendamento = (agendadoPara?: string): string => {
    if (!agendadoPara) return '-';
    // Formato antigo: "HH:mm" (apenas hora)
    if (/^\d{2}:\d{2}$/.test(agendadoPara)) return agendadoPara;
    // Formato novo: "YYYY-MM-DDTHH:mm" ou ISO
    return moment(agendadoPara).format("DD/MM/YYYY HH:mm");
};

interface TabelaPedidosProps {
    pedidos?: IPedido[],
    pedidosSelecionados?: IPedido[],
    marketplaces?: IMarketplace[];
    loadingMarketplaces?: boolean;
    loading?: boolean;
    abrirDetalhes: (uuid: string) => void;
    selecionarPedido: (pedido: IPedido) => void;
    /** Sessões de mesa (QR Code) com a conta pedida, indexadas pelo uuid do pedido. */
    contasSolicitadas?: Map<string, ISessaoMesa>;
}

export const TabelaPedidos = ({
    loading,
    pedidos,
    marketplaces,
    loadingMarketplaces,
    abrirDetalhes,
    selecionarPedido,
    pedidosSelecionados,
    contasSolicitadas
}: TabelaPedidosProps) => {
    const { dataHoraBr } = useDataHora();
    const { real } = useMoeda();
    const { titulo } = useLetras();

    const [paginaAtual, setPaginaAtual] = useState(0)
    const [linhasPorPagina, setLinhasPorPagina] = useState(10)
    const navigate = useNavigate();

    const indiceFinal = (paginaAtual + 1) * linhasPorPagina;
    const indiceInicial = indiceFinal - linhasPorPagina;
    const pedidosPaginaAtual = pedidos?.slice(indiceInicial, indiceFinal);

    if (loading) {
        return (
            <LoadingContainer>
                <CircularProgress size={50} color="primary" />
            </LoadingContainer>
        );
    }

    if (!pedidos?.length) {
        return (
            <NenhumPedidoContainer>
                <Typography>
                    Nenhum pedido encontrado
                </Typography>
            </NenhumPedidoContainer>
        );
    }

    const handlePaginaChange = (event: unknown, newPage: number) => {
        setPaginaAtual(newPage);
    };

    const handleLinhasPorPaginaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLinhasPorPagina(parseInt(event.target.value, 10));
        setPaginaAtual(0);
    };

    return (
        <>
            <TableContainer>
                <Table aria-label="Listagem de pedidos">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>Nº pedido</TableCell>
                            <TableCell>Nº diário</TableCell>
                            <TableCell>Nº externo</TableCell>
                            <TableCell>Data/hora</TableCell>
                            <TableCell>Agendamento</TableCell>
                            <TableCell>Pago</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Marketplace</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Nº itens</TableCell>
                            <TableCell>Avaliação</TableCell>
                            <TableCell>Contexto</TableCell>
                            <TableCell>Editar</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pedidosPaginaAtual?.map((pedido) => {
                            const pedidoSelecionadoIndex = pedidosSelecionados?.findIndex(pedidoSelecionado => pedidoSelecionado.uuid == pedido.uuid);
                            const marketplace = marketplaces?.find(marketplace => marketplace.uuid == pedido.marketplace?.uuid);
                            const contaSolicitada = contasSolicitadas?.get(pedido.uuid);
                            return (
                                <TableRow
                                    key={pedido.uuid}
                                    hover
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        cursor: 'pointer',
                                        ...(contaSolicitada && {
                                            backgroundColor: theme => alpha(theme.palette.warning.main, 0.12),
                                            boxShadow: theme => `inset 4px 0 0 ${theme.palette.warning.main}`,
                                        }),
                                    }}
                                    onClick={() => abrirDetalhes(pedido.uuid)}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            checked={pedidoSelecionadoIndex == -1 ? false : true}
                                            onClick={(e) => {
                                                selecionarPedido(pedido);
                                                e.stopPropagation();
                                            }}
                                            inputProps={{
                                                'aria-labelledby': `checkbox-${pedido.uuid}`,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {pedido.numeroPedido}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {pedido.numeroPedidoDiario}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {pedido.numeroPedidoExterno ?? "-"}
                                    </TableCell>
                                    <TableCell>{dataHoraBr(pedido.dataHora)}</TableCell>
                                    <TableCell>{formatarAgendamento(pedido.agendadoPara)}</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const { label, color } = getStatusPagamento(pedido.pagamentos);
                                            return <Chip label={label} color={color} size="small" />;
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        {contaSolicitada ? (
                                            <Tooltip title={`Pedida${contaSolicitada.contaSolicitadaPor ? ` por ${contaSolicitada.contaSolicitadaPor}` : ''}${contaSolicitada.contaSolicitadaAs ? ` às ${moment(contaSolicitada.contaSolicitadaAs).format("HH:mm")}` : ''} pelo QR Code`}>
                                                <Chip
                                                    icon={<ReceiptLong />}
                                                    label="Pediu a conta"
                                                    color="warning"
                                                    size="small"
                                                />
                                            </Tooltip>
                                        ) : (
                                            <StatusPedidoPilula status={pedido.status as StatusPedido} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {loadingMarketplaces
                                            ? <CircularProgress size={10} />
                                            : pedido.marketplace?.tipo == TipoMarketplace.PDV
                                                ? (
                                                    <Typography color={TipoMarketplaceCor.PDV}>
                                                        PDV
                                                    </Typography>
                                                )
                                                : marketplace
                                                    ? <Typography color={TipoMarketplaceCor[marketplace.tipo]}>
                                                        {marketplace?.nome}
                                                    </Typography>
                                                    : <Typography>Não encontrado</Typography>
                                        }
                                    </TableCell>
                                    <TableCell>{real(pedido.valorTotal)}</TableCell>
                                    <TableCell>
                                        {pedido.cliente && pedido.mesaId 
                                            ? `${pedido.cliente.nome} (Mesa ${pedido.mesaId})`
                                            : pedido.cliente 
                                                ? pedido.cliente.nome 
                                                : pedido.mesaId 
                                                    ? `Mesa ${pedido.mesaId}` 
                                                    : '-'
                                        }
                                    </TableCell>
                                    <TableCell>{pedido.produtos?.length}</TableCell>
                                    <TableCell><AvaliacaoPedidoResumo avaliacao={pedido.avaliacao} /></TableCell>
                                    <TableCell>{titulo(pedido.tipo)}</TableCell>
                                    <TableCell>
                                        {
                                        (pedido.status != StatusPedido.Cancelado) &&
                                        (pedido?.marketplace?.tipo == TipoMarketplace.PDV || pedido?.marketplace?.tipo == TipoMarketplace.CARDAPIO_DIGITAL_DELIVERY_RETIRADA)
                                            ? (
                                                <IconButton size={"small"} onClick={() => navigate(`/pedidos/operacao/edicao/${pedido.uuid}`)}>
                                                    <Edit fontSize="small" color="primary" />
                                                </IconButton>
                                            )
                                            : "-"
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={pedidos?.length || 0}
                rowsPerPage={linhasPorPagina}
                page={paginaAtual}
                onPageChange={handlePaginaChange}
                onRowsPerPageChange={handleLinhasPorPaginaChange}
                labelRowsPerPage="Linhas por página:"
                sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2, width: "100%" }}
            />
        </>
    );
};
